import * as React from 'react';

import {
  enumUnit,
  PurchaseLineItemType,
  Unit,
} from '@rematter/frontend-lib-api-client';
import { enumLoadWeightType } from '@rematter/modules/trade/types/load';
import { zeroWeight } from '@rematter/modules/trade/utils/weight';
import { TouchButton } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideSparkles } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

import { CatalyticConverterModal } from '../../../_shared/CatalyticConverterModal';
import { NormalizedLineItem } from '../_types';
import { EditCatalyticConverterDetailsDialog } from '../dialogs/EditCatalyticConverterDetailsDialog';

import { $inventoryItemsById } from '~/modules/trade/_state/loadForm.state';
import {
  $catalyticModalState,
  $closeCatalyticModal,
  $openCatalyticModal,
  $setAutoFocusRowId,
} from '~/modules/trade/_state/materialsInterface.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { useUIPreferences } from '~/shared/hooks';

const ICON_SPARKLES = <LucideSparkles />;

type AddCatalyticWidgetProps = {
  onAddItem?: (
    type: PurchaseLineItemType,
    data?: Partial<NormalizedLineItem>,
  ) => void;
  onUpdateItem?: (id: string, updates: Partial<NormalizedLineItem>) => void;
  modalStyle?: React.CSSProperties;
  buttonSize?: 'sm' | 'md' | 'lg' | 'xl';
};

// ⚛️ ⚛️ ⚛️ ADD CATALYTIC WIDGET ⚛️ ⚛️ ⚛️
export const AddCatalyticWidget: React.FC<AddCatalyticWidgetProps> = ({
  onAddItem,
  onUpdateItem,
  modalStyle,
  buttonSize,
}) => {
  // Translations
  const t = useTranslations();

  // Atoms
  const catalyticModalState = useAtomValue($catalyticModalState);
  const setAutoFocusRowId = useSetAtom($setAutoFocusRowId);
  const openCatalyticModal = useSetAtom($openCatalyticModal);
  const closeCatalyticModal = useSetAtom($closeCatalyticModal);
  const inventoryItemsById = useAtomValue($inventoryItemsById);

  // Data
  const { touchModeEnabled } = useUIPreferences();

  // Callbacks
  const handleAddCatalyticItem = React.useCallback(() => {
    if (!onAddItem) return;
    const newItemId = `temp-${uuidv4()}`;
    onAddItem('CatalyticConverter', {
      id: newItemId,
      weights: {
        [enumLoadWeightType.Gross]: zeroWeight(enumUnit.Each),
        [enumLoadWeightType.Tare]: zeroWeight(enumUnit.Each),
        [enumLoadWeightType.Net]: zeroWeight(enumUnit.Each),
      },
      pricingUnit: enumUnit.Each,
    });
    setAutoFocusRowId(newItemId);
  }, [onAddItem, setAutoFocusRowId]);

  const handleOpenCatalyticModal = React.useCallback(() => {
    openCatalyticModal({ mode: 'add', selectedItemId: null, lineItemId: null });
  }, [openCatalyticModal]);

  const handlePress = React.useCallback(() => {
    if (touchModeEnabled) {
      handleOpenCatalyticModal();
    } else {
      handleAddCatalyticItem();
    }
  }, [touchModeEnabled, handleOpenCatalyticModal, handleAddCatalyticItem]);

  const handleCatalyticItemSelect = React.useCallback(
    (materialId?: string) => {
      const lineItemId = catalyticModalState.isOpen
        ? catalyticModalState.lineItemId
        : null;
      const item = materialId ? inventoryItemsById[materialId] : undefined;
      const unit = (item?.effectivePricingUnit as Unit) ?? enumUnit.Each;

      if (lineItemId && onUpdateItem) {
        onUpdateItem(lineItemId, {
          itemId: materialId ?? null,
          pricingUnit: unit,
        });
        return;
      }

      if (!onAddItem) return;

      const newItemId = `temp-${uuidv4()}`;
      onAddItem('CatalyticConverter', {
        id: newItemId,
        itemId: materialId ?? null,
        weights: {
          [enumLoadWeightType.Gross]: zeroWeight(unit),
          [enumLoadWeightType.Tare]: zeroWeight(unit),
          [enumLoadWeightType.Net]: zeroWeight(unit),
        },
        pricingUnit: unit,
      });
      setAutoFocusRowId(null);
    },
    [
      catalyticModalState.isOpen,
      catalyticModalState.lineItemId,
      onUpdateItem,
      onAddItem,
      setAutoFocusRowId,
      inventoryItemsById,
    ],
  );

  const handleUpdateCatalyticConverters = React.useCallback(
    (data: {
      id: string;
      serialNumber?: string;
      oemCode?: string;
      compliance?: string;
    }) => {
      onUpdateItem?.(data.id, {
        catSerialNumber: data.serialNumber,
        catOemCode: data.oemCode,
        catComplianceStatus: data.compliance,
      });
    },
    [onUpdateItem],
  );

  const handleCatalyticOpenChange = React.useCallback(
    () => closeCatalyticModal(),
    [closeCatalyticModal],
  );

  return (
    <>
      <TouchButton
        onPress={handlePress}
        className="@AddItemButton w-full"
        isSelected={false}
        leftElement={ICON_SPARKLES}
        color="primary"
        variant="outline"
        size={buttonSize}
        aria-label="Add Item"
      >
        {t.scaleTicketPro.actions.addConverter}
      </TouchButton>

      <CatalyticConverterModal
        isOpen={catalyticModalState.isOpen}
        mode={catalyticModalState.isOpen ? catalyticModalState.mode : 'add'}
        initialSelectedItemId={
          catalyticModalState.isOpen ? catalyticModalState.selectedItemId : null
        }
        lineItemId={
          catalyticModalState.isOpen ? catalyticModalState.lineItemId : null
        }
        touchModeEnabled={touchModeEnabled}
        onOpenChange={handleCatalyticOpenChange}
        onSelectItem={handleCatalyticItemSelect}
        modalStyle={modalStyle}
      />

      <EditCatalyticConverterDetailsDialog
        onUpdateValues={handleUpdateCatalyticConverters}
      />
    </>
  );
};
